"use client";

import Link from "next/link";
import { ProcurementVisual } from "@/components/landing/ModuleVisuals";
import { CheckCircle2, ShoppingCart, Users, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface Feature {
  titleKey: string;
  descKey: string;
}

export default function ProcurementProductPage() {
  const { t } = useI18n();

  const features: Feature[] = [
    {
      titleKey: "pages.product.procurement.features.requests.title",
      descKey: "pages.product.procurement.features.requests.desc",
    },
    {
      titleKey: "pages.product.procurement.features.suppliers.title",
      descKey: "pages.product.procurement.features.suppliers.desc",
    },
    {
      titleKey: "pages.product.procurement.features.workflow.title",
      descKey: "pages.product.procurement.features.workflow.desc",
    },
    {
      titleKey: "pages.product.procurement.features.budget.title",
      descKey: "pages.product.procurement.features.budget.desc",
    },
    {
      titleKey: "pages.product.procurement.features.payment.title",
      descKey: "pages.product.procurement.features.payment.desc",
    },
    {
      titleKey: "pages.product.procurement.features.tracking.title",
      descKey: "pages.product.procurement.features.tracking.desc",
    },
  ];

  const workflow = [
    {
      step: "1",
      titleKey: "pages.product.procurement.workflow.request.title",
      descKey: "pages.product.procurement.workflow.request.desc",
    },
    {
      step: "2",
      titleKey: "pages.product.procurement.workflow.approval.title",
      descKey: "pages.product.procurement.workflow.approval.desc",
    },
    {
      step: "3",
      titleKey: "pages.product.procurement.workflow.order.title",
      descKey: "pages.product.procurement.workflow.order.desc",
    },
    {
      step: "4",
      titleKey: "pages.product.procurement.workflow.receive.title",
      descKey: "pages.product.procurement.workflow.receive.desc",
    },
    {
      step: "5",
      titleKey: "pages.product.procurement.workflow.invoice.title",
      descKey: "pages.product.procurement.workflow.invoice.desc",
    },
    {
      step: "6",
      titleKey: "pages.product.procurement.workflow.payment.title",
      descKey: "pages.product.procurement.workflow.payment.desc",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              {t("pages.product.procurement.headline") ||
                "Закупки. От заявки до оплаты."}
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.procurement.description") ||
                `Управляйте поставщиками, заказами и бюджетом.
                Автоматическое создание платёжных требований.
                Контроль расходов на каждом этапе.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild variant="default" className="h-12">
                <Link href="/contact">
                  {t("pages.product.procurement.cta") || "Попробовать бесплатно"}
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12">
                <Link href="/pricing">
                  {t("pages.product.procurement.pricing") || "Смотреть тарифы"}
                </Link>
              </Button>
            </div>
          </div>

          {/* Visual */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden h-96">
            <ProcurementVisual />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.product.procurement.featuresTitle") || "Основные возможности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 size={24} className="text-keiro-green flex-shrink-0 mt-0.5" />
                <h3 className="font-bold text-slate-900">
                  {t(feature.titleKey) || feature.titleKey}
                </h3>
              </div>
              <p className="text-slate-600">
                {t(feature.descKey) || feature.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.procurement.workflowTitle") ||
            "Полный цикл закупки в одном месте"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflow.map((item, idx) => (
            <div key={idx} className="relative">
              <div className="bg-keiro-mist border border-keiro-green/20 rounded-xl p-6">
                <div className="inline-block bg-keiro-green text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mb-3">
                  {item.step}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">
                  {t(item.titleKey) || item.titleKey}
                </h3>
                <p className="text-slate-600">
                  {t(item.descKey) || item.descKey}
                </p>
              </div>
              {idx < workflow.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 transform translate-y-1/2">
                  <div className="w-6 h-1 bg-keiro-green/20" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Supplier Management */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.procurement.supplierTitle") ||
                "Справочник поставщиков"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.procurement.supplierDesc") ||
                `Централизованный справочник всех поставщиков.
                Условия оплаты, банковские реквизиты, история заказов.
                Быстрый поиск по наименованию или категории.`}
            </p>
            <ul className="space-y-3">
              {[
                "Полная информация о поставщике",
                "История покупок и сроков оплаты",
                "Условия доставки и скидки",
                "Статус и рейтинги",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <Users size={20} className="text-keiro-green flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
            <p className="text-slate-600 text-center">
              {t("pages.product.procurement.supplierPlaceholder") ||
                "Интерфейс управления поставщиками"}
            </p>
          </div>
        </div>
      </section>

      {/* Budget Control */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 order-2 lg:order-1">
            <p className="text-slate-600 text-center">
              {t("pages.product.procurement.budgetPlaceholder") ||
                "Контроль бюджета в реальном времени"}
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.procurement.budgetTitle") ||
                "Контроль бюджета на каждом этапе"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.procurement.budgetDesc") ||
                `Установите лимиты по категориям и центрам затрат.
                При создании заказа система проверяет остаток.
                Превышения требуют дополнительного одобрения.`}
            </p>
            <ul className="space-y-3">
              {[
                "Лимиты по категориям закупок",
                "Мониторинг остатков бюджета",
                "Предупреждения при приближении к лимиту",
                "Расчёты с учётом планируемых заказов",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <Lock size={20} className="text-keiro-green flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Auto Payment Requests */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.procurement.paymentTitle") ||
            "Автоматическое создание платёжных требований"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="border border-slate-200 rounded-xl p-8">
            <ShoppingCart size={32} className="text-keiro-green mb-4" />
            <h3 className="font-bold text-slate-900 mb-3 text-lg">
              {t("pages.product.procurement.autoPayment1.title") ||
                "Получение товара"}
            </h3>
            <p className="text-slate-600">
              {t("pages.product.procurement.autoPayment1.desc") ||
                "Когда товар получен и качество проверено, создаётся платёжное требование."}
            </p>
          </div>
          <div className="border border-slate-200 rounded-xl p-8">
            <Lock size={32} className="text-keiro-green mb-4" />
            <h3 className="font-bold text-slate-900 mb-3 text-lg">
              {t("pages.product.procurement.autoPayment2.title") ||
                "Контроль качества"}
            </h3>
            <p className="text-slate-600">
              {t("pages.product.procurement.autoPayment2.desc") ||
                "Автоматическая сверка с накладной и счётом-фактурой."}
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.procurement.useCasesTitle") ||
            "Типовые сценарии"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.procurement.useCase1.title") || "Закупщик",
              desc: t("pages.product.procurement.useCase1.desc") || "Создавай заказы, отслеживай статус, общайся с поставщиками в системе.",
              emoji: "🛒",
            },
            {
              title: t("pages.product.procurement.useCase2.title") || "Управляющий отелем",
              desc: t("pages.product.procurement.useCase2.desc") || "Утверждай заказы по бюджету, видишь все расходы в одном месте.",
              emoji: "✅",
            },
            {
              title: t("pages.product.procurement.useCase3.title") || "Бухгалтер",
              desc: t("pages.product.procurement.useCase3.desc") || "Автоматические платёжные требования, сверка счётов, контроль расходов.",
              emoji: "📋",
            },
          ].map((useCase, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors"
            >
              <div className="text-3xl mb-3">{useCase.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-3">{useCase.title}</h3>
              <p className="text-slate-600">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.procurement.benefitsTitle") ||
            "Результаты использования"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: t("pages.product.procurement.benefits.time.title") || "Экономия времени",
              value: "10 часов/месяц",
              desc: t("pages.product.procurement.benefits.time.desc") || "Автоматизация документооборота",
            },
            {
              title: t("pages.product.procurement.benefits.errors.title") || "Меньше ошибок",
              value: "-90%",
              desc: t("pages.product.procurement.benefits.errors.desc") || "Сверка происходит автоматически",
            },
            {
              title: t("pages.product.procurement.benefits.visibility.title") || "Видимость расходов",
              value: "100%",
              desc: t("pages.product.procurement.benefits.visibility.desc") || "Каждый заказ в системе",
            },
            {
              title: t("pages.product.procurement.benefits.control.title") || "Контроль бюджета",
              value: "Реальный",
              desc: t("pages.product.procurement.benefits.control.desc") || "Нет перерасходов",
            },
          ].map((benefit, idx) => (
            <div
              key={idx}
              className="bg-keiro-mist rounded-xl p-8 border border-keiro-green/20 text-center"
            >
              <p className="text-sm text-slate-600 mb-3 uppercase tracking-wide font-semibold">
                {benefit.title}
              </p>
              <p className="text-3xl font-bold text-keiro-green-dark mb-3">
                {benefit.value}
              </p>
              <p className="text-sm text-slate-600">
                {benefit.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.product.procurement.ctaTitle") ||
              "Готовы упростить закупки?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.product.procurement.ctaDesc") ||
              "14 дней бесплатного доступа, без кредитной карты"}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.product.procurement.ctaButton") || "Начать бесплатно"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
